import { StoryFormData, STORY_TYPES, WORD_COUNT_OPTIONS } from '../../types/story';

interface ReviewConfirmProps {
  formData: StoryFormData;
  onEdit: (step: number) => void;
}

export default function ReviewConfirm({ formData, onEdit }: ReviewConfirmProps) {
  const storyType = STORY_TYPES.find((t) => t.value === formData.story_type);
  const wordCountOption = WORD_COUNT_OPTIONS.find(
    (w) => w.value === formData.word_count
  );

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-display font-bold text-gray-900 mb-2">
          Review Your Story
        </h2>
        <p className="text-gray-600">
          Make sure everything looks perfect before creating
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-4">
        {/* Story Type */}
        <div className="card">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">{storyType?.emoji}</span>
                <h3 className="text-lg font-display font-bold text-gray-900">
                  Story Type
                </h3>
              </div>
              <p className="text-gray-900 font-semibold">{storyType?.label}</p>
              <p className="text-sm text-gray-600 mt-1">
                {storyType?.description}
              </p>
            </div>
            <button
              onClick={() => onEdit(1)}
              className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 font-semibold"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Story Title */}
        <div className="card">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-display font-bold text-gray-900 mb-2">
                Story Title
              </h3>
              <p className="text-gray-900 font-semibold">{formData.title}</p>
            </div>
            <button
              onClick={() => onEdit(2)}
              className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 font-semibold"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Story Details */}
        <div className="card">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-display font-bold text-gray-900 mb-2">
                Story Details
              </h3>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                  {formData.story_input}
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {formData.story_input.length} characters
              </p>
            </div>
            <button
              onClick={() => onEdit(2)}
              className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 font-semibold"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Word Count */}
        <div className="card">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-display font-bold text-gray-900 mb-2">
                Story Length
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary-400 to-primary-600">
                  <span className="text-2xl font-display font-bold text-white">
                    {wordCountOption?.credits}
                  </span>
                </div>
                <div>
                  <p className="text-gray-900 font-semibold">
                    {wordCountOption?.label}
                  </p>
                  <p className="text-sm text-gray-600">
                    {formData.word_count.toLocaleString()} words •{' '}
                    {wordCountOption?.duration}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => onEdit(3)}
              className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 font-semibold"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Credit Cost Summary */}
        <div className="p-6 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-display font-bold">Credit Cost</h3>
            <div className="text-right">
              <p className="text-4xl font-display font-bold">
                {wordCountOption?.credits}
              </p>
              <p className="text-sm opacity-90">
                {wordCountOption?.credits === 1 ? 'credit' : 'credits'}
              </p>
            </div>
          </div>
          <div className="pt-4 border-t border-white/20">
            <p className="text-sm opacity-90">
              This will be deducted from your credit balance when you create the
              story.
            </p>
          </div>
        </div>

        {/* Important Notice */}
        <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">
                What happens next?
              </h4>
              <p className="text-sm text-blue-800 leading-relaxed">
                Once you confirm, our AI will start creating your personalized
                audiobook. This usually takes 3-5 minutes. You'll be able to listen
                and download it from your story library once it's ready.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
